/**
 * Canvas Layout Query
 *
 * Composable for fetching a canvas layout configuration from the backend by route identifier.
 * Falls back to a provided default config when no backend layout exists.
 * Also provides save (create/update) and delete mutations.
 */

import { useQuery } from "@vue/apollo-composable";
import {
  CanvasLayoutForRouteDocument,
  CanvasLayoutFieldsFragmentDoc,
  type CreateCanvasLayoutInput,
} from "~/graphql/generated/graphql";
import { useFragment } from "~/graphql/generated";
import type { CanvasConfig } from "~/helpers/canvas/canvas-types";
import { useCanvasLayoutMutations } from "./useCanvasLayoutMutations";
import { transformConfigToInput } from "~/composables/useCanvasLayoutTransform";

export interface UseCanvasLayoutQueryArgs {
  /** The route identifier to fetch the canvas layout for (e.g. "property.overview") */
  route: MaybeRefOrGetter<string>;
  /** Fallback canvas config used when the backend returns no layout */
  fallback: MaybeRefOrGetter<CanvasConfig>;
  /** Whether the query is enabled */
  enabled?: MaybeRefOrGetter<boolean>;
}

const BREAKPOINTS = ["xs", "sm", "md", "lg", "xl"] as const;

/**
 * Parses a backend WidgetResponsivePosition into the frontend
 * Partial<Record<WidgetBreakpoint, Partial<WidgetPosition>>> shape.
 */
function parseResponsivePosition(responsive: any): Record<string, any> | undefined {
  if (!responsive) return undefined;

  const result: Record<string, any> = {};
  for (const bp of BREAKPOINTS) {
    const override = responsive[bp];
    if (override) {
      result[bp.toUpperCase()] = {
        column: override.column ?? undefined,
        row: override.row ?? undefined,
        columnSpan: override.columnSpan ?? undefined,
        rowSpan: override.rowSpan ?? undefined,
      };
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Transforms a backend CanvasLayout response into a frontend CanvasConfig.
 *
 * The backend stores widget-specific properties in a generic `props` JSON field,
 * while the frontend CanvasConfig spreads those props directly onto the widget config.
 * Since the frontend now uses backend enum values directly, no type conversion is needed.
 */
function parseJsonField(value: unknown): any {
  if (value == null) return undefined;
  // Backend now returns JSON scalars as objects directly.
  // Keep string parsing as a safety net for any cached/legacy stringified data.
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
}

export function transformBackendLayout(backendLayout: any): CanvasConfig {
  return {
    id: backendLayout.id,
    name: backendLayout.name ?? undefined,
    layoutType: backendLayout.layoutType,
    layoutProps: parseJsonField(backendLayout.layoutProps),
    variables: parseJsonField(backendLayout.variables),
    canvases: (backendLayout.canvases ?? []).map((canvas: any) => ({
      name: canvas.name ?? undefined,
      layout: parseJsonField(canvas.layout),
      widgets: (canvas.widgets ?? []).map((widget: any) => {
        const props = parseJsonField(widget.props);
        return {
          // Spread the generic props first so explicit fields take precedence
          ...(props ?? {}),
          name: widget.name,
          label: widget.label ?? undefined,
          dataSource: widget.dataSource ?? undefined,
          type: widget.type,
          position: widget.position
            ? {
                column: widget.position.column,
                row: widget.position.row,
                columnSpan: widget.position.columnSpan ?? undefined,
                rowSpan: widget.position.rowSpan ?? undefined,
                responsive: parseResponsivePosition(widget.position.responsive),
              }
            : undefined,
          filterBindings: parseJsonField(widget.filterBindings),
          colorScheme: widget.colorScheme ?? undefined,
          hideOnBreakpoints: widget.hideOnBreakpoints ?? undefined,
        };
      }),
    })),
  };
}

/**
 * Query for fetching a canvas layout by route identifier.
 * Returns the backend layout if available, otherwise the provided fallback.
 */
export const useCanvasLayoutQuery = ({ route, fallback, enabled: _enabled = true }: UseCanvasLayoutQueryArgs) => {
  const enabled = computed(() => toValue(_enabled));

  const variables = computed(() => ({
    route: toValue(route),
  }));

  const query = useQuery(CanvasLayoutForRouteDocument, variables, () => ({
    enabled: enabled.value,
    fetchPolicy: "cache-first",
  }));

  /** The raw backend layout (null if none exists for this route) */
  const backendLayoutRaw = computed(() => query.result.value?.canvasLayoutForRoute ?? null);

  /** The unwrapped backend layout with fragment fields resolved */
  const backendLayout = computed(() => {
    if (!backendLayoutRaw.value) return null;
    return useFragment(CanvasLayoutFieldsFragmentDoc, backendLayoutRaw.value);
  });

  /** Whether a backend layout was found */
  const hasBackendLayout = computed(() => backendLayout.value !== null);

  /** The transformed backend config, or null if no backend layout exists */
  const backendConfig = computed(() => {
    if (!backendLayout.value) return null;
    return transformBackendLayout(backendLayout.value);
  });

  /**
   * The resolved canvas config.
   * Returns null while the initial query is in-flight so consumers can render
   * a loader instead of momentarily flashing the fallback config.
   * Once the query settles (result or error), this is the backend config if
   * one exists for this route, otherwise the fallback.
   */
  const canvasConfig = computed(() => {
    if (query.loading.value) return null;
    return backendConfig.value ?? toValue(fallback);
  });

  // --- Mutations ---

  const mutations = useCanvasLayoutMutations();

  /**
   * Save the canvas layout.
   * - If no backend layout exists yet, creates a new one for this route and marks it as default.
   * - If a backend layout already exists, updates it.
   *
   * Apollo cache is automatically updated from the mutation response —
   * no refetch needed (backend uses event-based architecture so refetch
   * would race against async processing).
   */
  async function saveLayout(config: CanvasConfig) {
    const routeValue = toValue(route);

    if (!hasBackendLayout.value) {
      // Create new layout
      const transformed = transformConfigToInput(config);
      const input: CreateCanvasLayoutInput = {
        route: routeValue,
        name: "", // Left empty for now. We can add a UI for editing this later if needed, but it's not important for the initial implementation since the route already serves as an identifier.
        isDefault: true,
        isShared: true, // All layouts are shared by default since we have no concept of user-specific layouts yet. We can add an "isShared" toggle in the UI later if needed.
        ...transformed,
      };

      await mutations.createLayout(input, {
        update: (cache, { data }) => {
          if (!data?.createCanvasLayout) return;
          cache.writeQuery({
            query: CanvasLayoutForRouteDocument,
            variables: { route: routeValue },
            data: { canvasLayoutForRoute: data.createCanvasLayout },
            overwrite: true,
          });
        },
      });
    } else {
      // Update existing layout
      await mutations.updateLayout(backendLayout.value!.id, config, {
        update: (cache, { data }) => {
          if (!data?.updateCanvasLayout) return;
          cache.writeQuery({
            query: CanvasLayoutForRouteDocument,
            variables: { route: routeValue },
            data: { canvasLayoutForRoute: data.updateCanvasLayout },
            overwrite: true,
          });
        },
      });
    }
  }

  /**
   * Delete the current backend canvas layout, reverting to the fallback.
   * No-op if no backend layout exists.
   */
  async function deleteLayout() {
    if (!hasBackendLayout.value || !backendLayout.value) return;

    const routeValue = toValue(route);

    await mutations.deleteLayout(backendLayout.value.id, {
      update: (cache) => {
        cache.writeQuery({
          query: CanvasLayoutForRouteDocument,
          variables: { route: routeValue },
          data: { canvasLayoutForRoute: null },
        });
      },
    });
  }

  return {
    // Query state
    loading: query.loading,
    error: query.error,
    result: query.result,
    refetch: query.refetch,

    // Resolved config
    canvasConfig,
    hasBackendLayout,
    backendLayout,

    // Mutations
    saveLayout,
    deleteLayout,
    saving: mutations.saving,
    deleting: mutations.deleting,
  };
};

export type CanvasLayoutQueryResult = ReturnType<typeof useCanvasLayoutQuery>;
